/**
 * @graphql agg
 */

import { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { ContentStatus } from '@confluence/page-utils/entry-points/enums';

import type {
	GraphQLContentStatus,
	useAllCommentsDataQuery$data,
	useAllCommentsDataQuery$variables,
} from './__generated__/useAllCommentsDataQuery.graphql';
import { findCommentInData } from './commentDataManagerUtils';

// Extracted types:
type Comments = NonNullable<useAllCommentsDataQuery$data['comments']>;
export type Comment = NonNullable<NonNullable<Comments>['nodes']>[number];
export type CommentAuthor = NonNullable<Comment>['author'];
export type CommentLocation = NonNullable<Comment>['location'];
export type CommentReactionsSummary = NonNullable<Comment>['reactionsSummary'];
export type CommentReplies = NonNullable<Comment>['replies'];
export type CommentReply = NonNullable<CommentReplies>[number];

type Modifiable<T> = { -readonly [P in keyof T]: T[P] };
type ModifiableCommentReplies = Modifiable<CommentReplies>;

// A hook to load all comments data for a page, with categorization and utility functions. This
// allows components to easily access and manage comments data, and also allows us to more easily
// manage updates to local cache in a single place vs. having "many sources of truth" for comments
// data that we had to try to keep in sync.  The downside is that this hook loads all comments data
// for a page, so it may be slower on pages with a large number of comments.

// Data Query Definitions:

// Unfortunately our Relay-friendly type generation tools don't support using GQL fragments yet -
// but in order to keep the definition of the comment fields in sync (so the cache update fragment
// is always in sync with the full query) we will need to manually make any changes to one
// definition in any others as well, until our tooling supports fragments and then we move to a
// more fragmented-based approach.  Note there are also comment queries or fragments in the
// following files that need to be kept in sync as well:
// - next/packages/comments-data/src/hooks/useCommentById.ts
// - next/packages/comments-data-manager/src/updateCommentInCache.ts

// Fragment for reusable comment data structure, used in main query and cache updates
// TODO: Uncommment this fragment when our tooling supports fragments being spread into queries.
// Note that if we are not allowed to share fragments between files/packages, we should move the
// parts of those other hooks/functions that use this fragment into this file.
// const useAllCommentsData_commentDataFragment = graphql`
// 	fragment useAllCommentsData_commentDataFragment on Comment {
// 		id
// 		author {
//			__typename
// 			displayName
// 			permissionType
// 			... on KnownUser {
// 				accountId
// 				permissionType
// 			}
// 			profilePicture {
// 				path
// 			}
// 			type
// 		}
// 		body(representation: ATLAS_DOC_FORMAT) {
// 			value
// 		}
// 		container {
// 			id
// 		}
// 		contentStatus
// 		createdAt {
// 			value
// 		}
// 		createdAtNonLocalized
// 		links {
// 			editui
// 			webui
// 		}
// 		location {
//			__typename
// 			type
// 			... on FooterComment {
// 				commentResolveProperties {
// 					resolved
// 					resolvedFriendlyDate
// 					resolvedTime
// 					resolvedUser
// 				}
// 			}
// 			... on InlineComment {
// 				inlineMarkerRef
// 				inlineResolveProperties {
// 					isDangling
// 					resolved
// 					resolvedFriendlyDate
// 					resolvedTime
// 					resolvedUser
// 				}
// 				inlineText
// 			}
// 		}
// 		parentId
// 		permissions {
// 			isEditable
// 			isRemovable
// 			isResolvable
// 		}
// 		reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
// 			ari
// 			containerAri
// 			reactionsCount
// 			reactionsSummaryForEmoji {
// 				id
// 				count
// 				emojiId
// 				reacted
// 			}
// 		}
// 		replies {
// 			id
// 			author {
//				__typename
// 				displayName
// 				permissionType
// 				... on KnownUser {
// 					accountId
// 				}
// 				profilePicture {
// 					path
// 				}
// 			}
// 			body(representation: ATLAS_DOC_FORMAT) {
// 				value
// 			}
// 			contentStatus
// 			createdAt {
// 				value
// 			}
// 			createdAtNonLocalized
// 			links {
// 				editui
// 				webui
// 			}
// 			location {
//				__typename
// 				type
// 				... on FooterComment {
// 					commentResolveProperties {
// 						resolved
// 						resolvedFriendlyDate
// 						resolvedTime
// 						resolvedUser
// 					}
// 				}
// 				... on InlineComment {
// 					inlineMarkerRef
// 					inlineResolveProperties {
// 						isDangling
// 						resolved
// 						resolvedFriendlyDate
// 						resolvedTime
// 						resolvedUser
// 					}
// 					inlineText
// 				}
// 			}
// 			parentId
// 			permissions {
// 				isEditable
// 				isRemovable
// 				isResolvable
// 			}
// 			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
// 				ari
// 				containerAri
// 				reactionsCount
// 				reactionsSummaryForEmoji {
// 					id
// 					count
// 					emojiId
// 					reacted
// 				}
// 			}
// 			version {
// 				by {
//					__typename
// 					displayName
// 				}
// 				number
// 				when
// 			}
// 		}
// 		version {
// 			by {
//				__typename
// 				type
// 				displayName
// 				... on KnownUser {
// 					accountId
// 				}
// 			}
// 			friendlyWhen
// 			number
// 			when
// 		}
// 	}
// `;

// Main GraphQL query with fragment spread
// TODO: Replace with fragment when our tooling supports it
export const allCommentsDataQuery = graphql`
	query useAllCommentsDataQuery($pageId: ID!, $contentStatus: [GraphQLContentStatus]) {
		comments(pageId: $pageId, contentStatus: $contentStatus, depth: ROOT) {
			nodes {
				id
				author {
					__typename
					displayName
					permissionType
					... on KnownUser {
						accountId
						permissionType
					}
					profilePicture {
						path
					}
					type
				}
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				container {
					id
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					editui
					webui
				}
				location {
					__typename
					type
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							isDangling
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
						inlineText
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
					ari
					containerAri
					reactionsCount
					reactionsSummaryForEmoji {
						id
						count
						emojiId
						reacted
					}
				}
				replies {
					id
					author {
						__typename
						displayName
						permissionType
						... on KnownUser {
							accountId
						}
						profilePicture {
							path
						}
					}
					body(representation: ATLAS_DOC_FORMAT) {
						value
					}
					contentStatus
					createdAt {
						value
					}
					createdAtNonLocalized
					links {
						editui
						webui
					}
					location {
						__typename
						type
						... on FooterComment {
							commentResolveProperties {
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
						... on InlineComment {
							inlineMarkerRef
							inlineResolveProperties {
								isDangling
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
							inlineText
						}
					}
					parentId
					permissions {
						isEditable
						isRemovable
						isResolvable
					}
					reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
						ari
						containerAri
						reactionsCount
						reactionsSummaryForEmoji {
							id
							count
							emojiId
							reacted
						}
					}
					version {
						by {
							__typename
							displayName
						}
						number
						when
					}
				}
				version {
					by {
						__typename
						type
						displayName
						... on KnownUser {
							accountId
						}
					}
					friendlyWhen
					number
					when
				}
			}
			totalCount
			__typename
		}
	}
`;

/**
 * Builds consistent query variables for fetching comments data.
 * This ensures we always use the same variable structure across the app.
 *
 * @param contentId - The ID of the content to get comments for
 * @returns Query variables object with pageId and contentStatus
 */
export const buildUseAllCommentsDataQueryVariables = (
	contentId: string,
): useAllCommentsDataQuery$variables => ({
	pageId: contentId,
	// Fetch all comments regardless of status, we will manually filter/categorize them
	contentStatus: ['DRAFT', 'CURRENT', 'ARCHIVED', 'DELETED'],
});

/*
 * Helper function to determine the appropriate content status values to use for comments queries
 * based on the page state (archived, view mode, etc.)
 *
 * This mirrors the logic in buildConsistentActiveCommentsQueryVariables to ensure consistency
 * between legacy queries and the new useAllCommentsData hook.
 *
 * @param contentStatus - The current content status of the page (e.g., 'archived', 'current', 'draft')
 * @returns Array of GraphQLContentStatus values to use for filtering comments
 */
export const buildDefaultCommentsContentStatusFilters = (
	contentStatus?: string | null,
): GraphQLContentStatus[] => {
	// If contentStatus is not provided, default to CURRENT only
	if (!contentStatus) {
		return ['CURRENT'];
	}

	// If the page is archived, include ARCHIVED and DRAFT comments
	if (contentStatus === ContentStatus.ARCHIVED) {
		return ['ARCHIVED', 'DRAFT'];
	}

	// For all other pages (current, draft pages being edited), include CURRENT and DRAFT comments
	return ['CURRENT', 'DRAFT'];
};

// Utility function to check if a comment is resolved
export const isCommentResolved = (comment: Comment): boolean => {
	const location = comment?.location;
	if (location) {
		if ('inlineResolveProperties' in location) {
			return location.inlineResolveProperties?.resolved ?? false;
		} else if ('commentResolveProperties' in location) {
			return location.commentResolveProperties?.resolved ?? false;
		}
	}
	return false;
};

// Mirrored types to avoid imports from useCommentsData
// <DANGER> Until useCommentsData is removed, these have the potential to get out of sync
export type ReplyData = CommentReply & {
	isUnread: boolean; // This is no longer used, but here for type safety with legacy CommentsDataMap
	wasRemovedByAnotherUser: boolean; // This is no longer used, but here for type safety with legacy CommentsDataMap
};

export type CommentData = Comment & {
	type: CommentType;
	isOpen: boolean;
	isUnread: boolean; // This is no longer used, but here for type safety with legacy CommentsDataMap
	wasRemovedByAnotherUser: boolean; // This is no longer used, but here for type safety with legacy CommentsDataMap
	replies: ReplyData[];
};

export enum CommentType {
	INLINE = 'inline',
	GENERAL = 'general',
}

export type CommentsDataMap = {
	inline: Record<string, CommentData>;
	general: Record<string, CommentData>;
};

export type CategorizedComments = {
	allFilteredComments: Comment[];
	openInlineComments: Comment[];
	openFooterComments: Comment[];
	resolvedComments: Comment[];
	openComments: Comment[];
	commentsDataMap: CommentsDataMap;
	hasNestedComments: boolean;
};

// Utility function to parse hasNestedComments from commentsDataMap
export const parseHasNestedComments = (commentsDataMap: CommentsDataMap): boolean => {
	try {
		const allGeneralComments = Object.values(commentsDataMap.general);

		// To see if there are nested comments, we need to check whether there are any replies whose parentID is not equal to the parent Comment's ID
		// This is because commentsDataMap.general is a flat map of parent comment --> everything below it
		// But each comment has it's own parent id which may be different from it's top level parent
		return allGeneralComments.some((comment) => {
			return comment?.replies?.some((reply) => reply && reply.parentId !== comment.id);
		});
	} catch (error) {
		// Default to false if somehow something goes wrong
		return false;
	}
};

// Utility function to categorize comments by type
export const categorizeComments = (
	comments: Comment[],
	contentStatusFilters: GraphQLContentStatus[],
): CategorizedComments => {
	const allFilteredComments: Comment[] = [];
	const openComments: Comment[] = [];
	const openInlineComments: Comment[] = [];
	const openFooterComments: Comment[] = [];
	const resolvedComments: Comment[] = [];

	// Transform array-based data to map structure for legacy compatibility
	const commentsDataMap: CommentsDataMap = {
		inline: {},
		general: {},
	};

	for (const comment of comments) {
		// Filter by content status, which is a string literal type (that is, a set of allowed
		// strings), so be cautious if the definitions or schema change
		if (
			contentStatusFilters.includes(comment?.contentStatus.toUpperCase() as GraphQLContentStatus) // Note that this implicitly filters out null / undefined comments, since contentStatusFilters does not contain null / undefined
		) {
			allFilteredComments.push(comment);

			const isOpen = !isCommentResolved(comment);
			const commentType =
				comment!.location?.type === 'INLINE' ? CommentType.INLINE : CommentType.GENERAL;

			const commentWithProperties: CommentData = {
				...comment!,
				type: commentType,
				isOpen,
				isUnread: false,
				wasRemovedByAnotherUser: false,
				replies: comment!.replies
					?.filter((reply: CommentReply) => reply !== null && reply !== undefined)
					.map((reply: CommentReply) => ({
						...reply,
						isUnread: false,
						wasRemovedByAnotherUser: false,
					})) as ReplyData[],
			};

			if (isOpen) {
				openComments.push(comment);

				if (comment?.location?.type === 'INLINE') {
					openInlineComments.push(comment);
				} else if (comment?.location?.type === 'FOOTER') {
					openFooterComments.push(comment);
				}
				// else unknown or missing location type, do nothing
			} else {
				resolvedComments.push(comment);
			}

			if (comment!.location?.type === 'INLINE') {
				const threadKey =
					'inlineMarkerRef' in comment!.location
						? comment!.location.inlineMarkerRef || comment!.id
						: comment!.id;
				commentsDataMap.inline[threadKey] = commentWithProperties;
			} else if (comment!.location?.type === 'FOOTER') {
				commentsDataMap.general[comment!.id] = commentWithProperties;
			}
		}
	}

	const hasNestedComments = parseHasNestedComments(commentsDataMap);

	return {
		allFilteredComments,
		openComments,
		openInlineComments,
		openFooterComments,
		resolvedComments,
		commentsDataMap,
		hasNestedComments,
	};
};

// Utility type for comparing content status arrays
// Because arrays are passed as reference types, we need to be careful about equality checks or we
// may end up in infinite loops in useEffect or useMemo if the array reference changes even though
// the contents are the same.
const areContentStatusArraysEqual = (
	contentStatus1: GraphQLContentStatus[],
	contentStatus2: GraphQLContentStatus[],
) => {
	if (contentStatus1.length !== contentStatus2.length) {
		return false;
	}
	for (const status1 of contentStatus1) {
		if (!contentStatus2.includes(status1)) {
			return false;
		}
	}
	return true;
};

// Utilities for deleting comments from cache:

type DeleteCommentUtilsProps = {
	cachedRootComments: Comment[];
	totalCount: number;
	commentId: string;
	repliesCount: number;
};

type DeleteCommentUtilsReturn = {
	updatedRootComments: Comment[];
	updatedTotalCount: number;
	error?: Error;
};

const deleteRootCommentInCache = ({
	cachedRootComments,
	totalCount,
	commentId,
	repliesCount,
}: DeleteCommentUtilsProps): DeleteCommentUtilsReturn => {
	let updatedTotalCount = totalCount;

	// Remove the newly resolved comment from the list if we can find it
	// Create a copy of the root comment array without the deleted reply, as the original is readonly
	const updatedRootComments = cachedRootComments.filter((comment) => comment?.id !== commentId);
	// Decrement total count - currently we can't delete a reply that has any comments, but
	// let's be safe and adjust for any replies in case this ever changes
	updatedTotalCount -= 1 + repliesCount;

	return {
		updatedRootComments,
		updatedTotalCount,
		error: undefined,
	};
};

const deleteCommentReplyInCache = ({
	cachedRootComments,
	totalCount,
	commentId,
	repliesCount,
	parentId,
}: DeleteCommentUtilsProps & { parentId: string }): DeleteCommentUtilsReturn => {
	// For replies, we need to remove the reply from the parent's replies list in the main
	// query's cache
	let updatedTotalCount = totalCount;
	let error: Error | undefined = undefined;

	// First, find the parent comment
	const parentComment = findCommentInData(cachedRootComments, parentId);
	if (parentComment) {
		// Create a copy of the parent's replies array without the deleted reply, as the original
		// is readonly
		const updatedReplies = (parentComment.replies || []).filter((reply) => reply?.id !== commentId);
		// Coerce the readonly replies array to be modifiable so we don't have to create new
		// modifiable comment objects all the way up the parent chain
		(parentComment.replies as ModifiableCommentReplies) = updatedReplies;

		// Decrement total count. Currently we can't delete a reply that has any comments. Also
		// since we can’t have spread fragments in Relay-friendly queries yet, the current
		// CommentReply type doesn't have a `replies` field. And the backend, sends the nested
		// replies to us in a flat list with the correct parentIDs so the FE must manually nest
		// them. But let's adjust the count for replies just in case this ever changes.
		updatedTotalCount -= 1 + repliesCount;
	} else {
		// Parent comment not found
		error = Error(
			`Cannot delete reply comment in cache: can not find parent comment with ID ${parentId}`,
		);
	}

	return {
		updatedRootComments: cachedRootComments,
		updatedTotalCount,
		error,
	};
};

const removeCommentFromCache = ({
	cachedRootComments,
	commentId,
	totalCount,
}: {
	cachedRootComments: Comment[];
	commentId: string;
	totalCount: number;
}): DeleteCommentUtilsReturn => {
	// Find the comment to delete to determine if it's a root comment or a reply
	const commentToDelete = findCommentInData(cachedRootComments, commentId);
	if (!commentToDelete) {
		return {
			updatedRootComments: [],
			updatedTotalCount: 0,
			error: new Error(`Cannot delete comment in cache: comment with ID ${commentId} not found`),
		};
	}

	// Update the appropriate part of the cache depending on whether it's a root comment or a reply
	const parentId = commentToDelete.parentId;
	return !parentId
		? deleteRootCommentInCache({
				cachedRootComments,
				totalCount,
				commentId,
				repliesCount: commentToDelete.replies?.length || 0,
			})
		: deleteCommentReplyInCache({
				cachedRootComments,
				totalCount,
				commentId,
				repliesCount: commentToDelete.replies?.length || 0,
				parentId,
			});
};

export type FetchPolicies =
	| 'cache-first'
	| 'cache-only'
	| 'cache-and-network'
	| 'network-only'
	| 'no-cache';

export type UseAllCommentsDataOptions = {
	fetchPolicy?: FetchPolicies;
	onCompleted?: (data: useAllCommentsDataQuery$data) => void;
	onError?: (error: Error) => void;
	skip?: boolean;
};

export type UseAllCommentsDataProps = {
	contentId: string;
	contentStatus?: GraphQLContentStatus[];
	options?: UseAllCommentsDataOptions;
};

type DeleteCommentInCacheProps = {
	commentId: string;
	onError?: (error: Error) => void;
};

export type UseAllCommentsDataReturn = CategorizedComments & {
	loading: boolean;
	error: Error | undefined;
	refetch: () => Promise<any>;
	allComments: Comment[];
	commentCount: number;
	hasAnyComments: boolean;
	deleteCommentInCache: ({ commentId, onError }: DeleteCommentInCacheProps) => void;
	isDataProcessed: boolean;
};

/**
 * Custom React hook for loading all GraphQL comments data for a page
 *
 * Features:
 * - Loads all comments (inline and footer) for the current page
 * - Provides categorized comment data (inline, footer, resolved, unresolved)
 * - Handles loading states and errors gracefully
 * - Supports various fetch policies and options
 * - Includes comprehensive type safety
 *
 * @param options - Configuration options for the hook
 * @returns Object containing comments data, loading state, and utility functions
 */
export const useAllCommentsData = ({
	contentId,
	contentStatus = ['CURRENT'],
	options = {},
}: UseAllCommentsDataProps): UseAllCommentsDataReturn => {
	const { fetchPolicy = 'cache-first', onCompleted, onError } = options;
	const contentStatusRef = useRef<GraphQLContentStatus[]>([]);

	// Check if contentStatus array has changed.  Content status is passed as an array by reference,
	// and anonymous functions may recreate the array with a new reference on each render, so we need
	// to check the contents for equality to avoid infinite loops in useEffect/useMemo below.
	const hasContentStatusChanged = !areContentStatusArraysEqual(
		contentStatusRef.current,
		contentStatus,
	);
	contentStatusRef.current = contentStatus;

	const [processedData, setProcessedData] = useState<{
		allComments: Comment[];
		allFilteredComments: Comment[];
		openInlineComments: Comment[];
		openFooterComments: Comment[];
		resolvedComments: Comment[];
		openComments: Comment[];
		commentsDataMap: CommentsDataMap;
		hasNestedComments: boolean;
		isDataProcessed: boolean;
	}>({
		allComments: [],
		allFilteredComments: [],
		openInlineComments: [],
		openFooterComments: [],
		resolvedComments: [],
		openComments: [],
		commentsDataMap: { inline: {}, general: {} },
		hasNestedComments: false,
		isDataProcessed: false,
	});

	const handleQueryError = useCallback(
		(error: Error) => {
			// Handle specific GraphQL errors gracefully
			// WS-3494 - If the error isn't related to reactionsSummary returning null, just mark it handled
			// COMMENTS-188 - Or if the error isn't related to cc-pages not being reachable just mark it handled
			if (
				error?.message?.search(/reactions/i) !== -1 ||
				error?.message?.search(/cc-pages/i) !== -1
			) {
				markErrorAsHandled(error);
			}
			onError?.(error);
		},
		[onError],
	);

	// Skip query if no content ID or explicitly skipped
	const shouldSkip = !contentId || options.skip;

	const queryResponse = useQueryInterop<
		useAllCommentsDataQuery$data,
		useAllCommentsDataQuery$variables
	>(allCommentsDataQuery, {
		variables: buildUseAllCommentsDataQueryVariables(contentId ?? ''),
		skip: shouldSkip,
		fetchPolicy,
		onCompleted: (data: useAllCommentsDataQuery$data) => {
			onCompleted?.(data);
		},
		onError: handleQueryError,
	});

	// Process and categorize comments when data changes
	useEffect(() => {
		// Don't process data while loading
		// This prevents any issues where data changes briefly during the loading state, which can lead to flickering
		if (queryResponse.loading) {
			return;
		}

		if (queryResponse.data?.comments?.nodes) {
			const allComments = (queryResponse.data?.comments?.nodes || []).filter(
				(node: Comment | null) => node !== null,
			);
			const categorized = categorizeComments(allComments, contentStatus);

			setProcessedData({
				allComments,
				...categorized,
				isDataProcessed: true,
			});
		} else {
			setProcessedData({
				allComments: [],
				allFilteredComments: [],
				openInlineComments: [],
				openFooterComments: [],
				resolvedComments: [],
				openComments: [],
				commentsDataMap: { inline: {}, general: {} },
				hasNestedComments: false,
				isDataProcessed: true, // Even if no data, we've processed the empty state
			});
		}
		// Only reprocess if data changes or contentStatus contents has changed (not just the reference)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryResponse.data, queryResponse.loading, hasContentStatusChanged]);

	// Memoized computed values for performance
	// Note that commentCount and hasAnyComments are based on filtered (by contentStatus) comments
	// from the query, just like GQL results would look like if we had passed a contentStatus filter
	// to the query itself.
	const commentCount = useMemo(
		() => processedData.allFilteredComments.length,
		[processedData.allFilteredComments.length],
	);
	const hasAnyComments = useMemo(() => commentCount > 0, [commentCount]);

	// Refetch function with error handling
	const refetch = useCallback(async () => {
		try {
			return await queryResponse.refetch();
		} catch (error) {
			handleQueryError(error as Error);
		}
	}, [queryResponse, handleQueryError]);

	const deleteCommentInCache = useCallback(
		({ commentId, onError }: DeleteCommentInCacheProps) => {
			// First, we try to find the comment to determine if it's a root comment or a reply.  If it's a
			// root comment, we need to remove it from the root comments list and update the main query's
			// cache and totalCount. If it's a reply, we need to update its parent comments's replies list
			// in the cache - but we still  update the whole main query (vs. just update the parent comment
			// itself via a fragment update) because we need to update the main query's totalCount in the
			// cache to keep everything in sync.
			try {
				// Read the (root level) query from the cache
				const dataProxy: useAllCommentsDataQuery$data | null = readQueryInterop<
					useAllCommentsDataQuery$data,
					useAllCommentsDataQuery$variables
				>({
					query: allCommentsDataQuery,
					variables: buildUseAllCommentsDataQueryVariables(contentId),
				});

				// Make a shallow copy that's not readonly if there are nodes (which are readonly),
				// otherwise use an empty array
				const nodes: Comment[] = dataProxy?.comments?.nodes ? [...dataProxy?.comments?.nodes] : [];
				const cachedRootComments: Comment[] = nodes.filter(
					(comment: Comment | null | undefined) => !!comment,
				);
				const totalCount = dataProxy?.comments?.totalCount || 0;

				const { updatedRootComments, updatedTotalCount, error } = removeCommentFromCache({
					cachedRootComments,
					commentId,
					totalCount,
				});

				if (error) {
					onError?.(error);
					return;
				}

				writeQueryInterop<useAllCommentsDataQuery$data, useAllCommentsDataQuery$variables>({
					query: allCommentsDataQuery,
					variables: buildUseAllCommentsDataQueryVariables(contentId),
					data: {
						comments: {
							nodes: updatedRootComments,
							totalCount: updatedTotalCount,
							__typename: 'PaginatedCommentList',
						},
					},
				});
			} catch (err) {
				onError?.(
					new Error(`An error occurred when deleting cached comment with ID ${commentId}: ${err}`),
				);
			}
		},
		[contentId],
	);

	return {
		loading: queryResponse.loading,
		error: queryResponse.error,
		refetch,
		...processedData,
		isDataProcessed: processedData.isDataProcessed,
		commentCount,
		hasAnyComments,
		deleteCommentInCache,
	};
};

export type FetchAllCommentsDataProps = {
	contentId: string;
	contentStatus?: GraphQLContentStatus[];
};

/**
 * Fetches all comments data for a page by executing the GraphQL query and returning categorized results.
 * This function awaits the query to complete and returns data equivalent to categorizeComments results.
 * You should use it ONLY when querying for comments data outside the scope of a component
 *
 * @param contentId - The ID of the content to get comments for
 * @param contentStatus - Optional array of content statuses to filter by
 * @returns Promise that resolves to categorized comment data
 */
export const fetchAllCommentsData = async ({
	contentId,
	contentStatus = ['CURRENT'],
}: FetchAllCommentsDataProps): Promise<CategorizedComments> => {
	const variables = buildUseAllCommentsDataQueryVariables(contentId);

	try {
		// Execute the query and wait for data to come back
		const data = await queryInterop<useAllCommentsDataQuery$data>({
			query: allCommentsDataQuery,
			variables,
			fetchPolicy: 'cache-first',
		});

		if (!data?.data?.comments?.nodes) {
			return {
				allFilteredComments: [],
				openComments: [],
				openInlineComments: [],
				openFooterComments: [],
				resolvedComments: [],
				commentsDataMap: {
					inline: {},
					general: {},
				},
				hasNestedComments: false,
			};
		}

		const filteredNodes = data.data.comments.nodes.filter((node): node is Comment => node !== null);

		// Categorize the comments using the existing categorizeComments function
		return categorizeComments(filteredNodes, contentStatus);
	} catch (error) {
		// Query error - return empty categorized data
		// [Optional - Add some custom logic here] - caller handles error
		throw error;
	}
};
